<script lang="ts">
  import Spinner from '@/components/anim/spinner.svelte'
  import CheckMark from '@/components/icons/check-mark.svelte'
  import TextField from '@/components/text-field.svelte'
  import { createFormContext } from '@/context/form'
  import Footer from '@/layouts/footer.svelte'
  import Main from '@/layouts/main.svelte'
  import { ssoSignUp } from '@/lib/api'
  import { getTermsAndPrivacyUrlByAudience } from '@/lib/helpers'
  import { Dialog, showDialog } from '@/lib/native-messaging/dialog'
  import { logComponentEvent } from '@/lib/native-messaging/log-event'
  import { dispatchWebViewAction } from '@/lib/native-messaging/web-view-action'
  import { redirect } from '@/lib/oauth/redirect'
  import { cn } from '@/lib/utils/cn'
  import type { Audience } from '@/params'
  import { SSO_SignUpForm } from '@/schema'
  import { router } from '@inertiajs/svelte'
  import clsx from 'clsx'
  import { onMount } from 'svelte'
  import { _ } from 'svelte-i18n'

  const params = new URLSearchParams(window.location.search)
  const payloadJsonStr = params.get('payload')
  let payload: null | {
    redirect_uri: string
    audience: Audience
    name: string
    is_binding_flow?: string // 'true'
  } = null
  let redirectUri = ''
  if (payloadJsonStr) {
    try {
      payload = JSON.parse(payloadJsonStr)
      redirectUri =
        new URL(payload!['redirect_uri']).searchParams.get('redirect_uri') ?? ''
    } catch {
      console.error('failed to parse payload')
    }
  }
  const audience = payload?.audience ?? null
  const username = payload?.name ?? ''
  const isBindingFlow = !!payload?.['is_binding_flow']

  console.log({
    audience,
    username,
    isBindingFlow,
  })

  let isAgreed = false
  let isLoading = false

  const { validate, allTouched, hasError } = createFormContext({
    schema: SSO_SignUpForm,
    t: $_,
  })

  async function handleSubmit(
    event: SubmitEvent & {
      currentTarget: EventTarget & HTMLFormElement
    },
  ) {
    const data = validate(new FormData(event.currentTarget))
    isLoading = true
    const [result, response] = await ssoSignUp({
      ...data,
      ...Object.fromEntries(params),
    })
    isLoading = false
    switch (result) {
      case 'SUCCESS':
        if ((response as { success?: boolean }).success) {
          const bindUrl = new URL(payload?.['redirect_uri'] || location.href)
          bindUrl.pathname = '/bind'
          bindUrl.searchParams.delete('success')
          bindUrl.searchParams.delete('error')
          bindUrl.searchParams.delete('error_description')
          bindUrl.searchParams.set('is_binding_flow', 'true')
          router.visit(bindUrl.pathname + bindUrl.search)
        } else {
          redirect(redirectUri, {
            ...payload,
            ...(response as object),
          })
          dispatchWebViewAction({ action: 'end_session' })
        }
        break
      case 'EMAIL_USED':
        showDialog(Dialog.EmailUsed)
        break
      case 'SSO_NO_EMAIL':
        showDialog(Dialog.AppleIDNoEmail)
        break
      case 'NETWORK_ERROR':
        showDialog(Dialog.InternetDisconnected)
        break
      case 'UNEXPECTED_HTTP_ERROR':
        showDialog(Dialog.UnhandledHTTPError(response))
        break
      case 'UNEXPECTED_ERROR':
        showDialog(Dialog.UnhandledError(response.message))
        break
    }
  }

  onMount(() => {
    try {
      const { terms, privacy } = getTermsAndPrivacyUrlByAudience(audience)

      document.querySelector('span#terms')?.addEventListener('click', () => {
        dispatchWebViewAction({
          action: 'push',
          url: terms,
          navBarTitle: '',
          navBarVisible: true,
        })
      })
      document.querySelector('span#privacy')?.addEventListener('click', () => {
        dispatchWebViewAction({
          action: 'push',
          url: privacy,
          navBarTitle: '',
          navBarVisible: true,
        })
      })
    } catch {
      // FIXME: in binding flow, native won't provide `audience` query param
    }
  })

  const policyParagrahParts = $_('onboarding_terms_privacy_policy')
    .split(/({[^}]+})/)
    .filter(Boolean)
</script>

<form
  on:submit|preventDefault={handleSubmit}
  class={cn('flex h-full flex-col', isBindingFlow && 'pt-[100px]')}
>
  <Main>
    <TextField
      name="username"
      placeholder={$_('placeholder_settings_name')}
      maxlength={30}
      dataFieldName="username"
      defaultValue={username}
    />
  </Main>
  <Footer>
    <div class="grid grid-cols-[auto_1fr] items-start space-x-[8px]">
      <div class="relative h-4 w-4">
        <input
          type="checkbox"
          class={clsx(
            'absolute inset-0 appearance-none rounded-[4px] border-content-placeholder text-white',
            isAgreed ? 'bg-secondary' : 'border-[1.5px]',
          )}
          checked={isAgreed}
          on:change={(event) => {
            isAgreed = event.currentTarget.checked
            logComponentEvent({
              type: 'checkbox',
              appAction: isAgreed ? 'check' : 'uncheck',
              componentName: 'signup_policy',
            })
          }}
        />
        {#if isAgreed}
          <span
            class="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <CheckMark />
          </span>
        {/if}
      </div>
      <p class="text-content-secondary text-label-md">
        {#each policyParagrahParts as part}
          {#if part.startsWith('{')}
            {#if part === '{terms}'}
              <span id="terms" class="text-secondary underline"
                >{$_('onboarding_terms')}</span
              >
            {:else if part === '{privacy_policy}'}
              <span id="privacy" class="text-secondary underline"
                >{$_('onboarding_privacy_policy')}</span
              >
            {/if}
          {:else}
            {part}
          {/if}
        {/each}
      </p>
    </div>
    <button
      type="submit"
      class="btn"
      disabled={!isAgreed || isLoading || !$allTouched || $hasError}
      on:click={() => {
        logComponentEvent({
          type: 'btn',
          appAction: 'click',
          componentName: 'continue_signup',
        })
      }}
    >
      {#if isLoading}
        <Spinner />
      {/if}
      <span>
        {$_('btn_next')}
      </span>
    </button>
  </Footer>
</form>
